import type { ReactNode } from 'react';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

export interface BadgeProps {
  tone?: BadgeTone;
  /** Render a leading status dot. */
  dot?: boolean;
  children: ReactNode;
}

export function Badge({ tone = 'neutral', dot = false, children }: BadgeProps): JSX.Element {
  const toneClasses = {
    neutral:
      'bg-zinc-100 text-zinc-700 border-zinc-200 dark:bg-zinc-800/50 dark:text-zinc-300 dark:border-white/5',
    accent:
      'bg-brand-50 text-brand-600 border-brand-200 dark:bg-brand-500/20 dark:text-brand-300 dark:border-brand-500/20 shadow-none dark:shadow-[0_0_10px_rgba(37,99,168,0.35)]',
    success:
      'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/20',
    warning:
      'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/20',
    danger:
      'bg-rose-50 text-rose-600 border-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/20',
    info: 'bg-blue-50 text-blue-600 border-blue-200 dark:bg-zinc-800/80 dark:text-zinc-300 dark:border-white/10',
  };

  const dotClasses = {
    neutral: 'bg-zinc-400',
    accent: 'bg-brand-500 dark:bg-brand-400 dark:shadow-[0_0_8px_rgba(61,125,196,0.8)]',
    success: 'bg-emerald-500 dark:bg-emerald-400',
    warning: 'bg-amber-500 dark:bg-amber-400',
    danger: 'bg-rose-500 dark:bg-rose-400',
    info: 'bg-blue-500 dark:bg-zinc-400',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${toneClasses[tone]}`}
    >
      {dot && (
        <span className={`w-1.5 h-1.5 rounded-full ${dotClasses[tone]}`} aria-hidden="true" />
      )}
      {children}
    </span>
  );
}
